/**
 * CollisionUtils - Shared collision detection utilities
 *
 * Used by both server (for projectile collision) and client (for visual prediction).
 * Works with world object data loaded from Tiled TMJ maps.
 *
 * Coordinate system:
 * - World objects have x, y (center position in world)
 * - Collision boxes have x, y (offset from object center), width, height
 * - Collision box position = object position + collision offset
 */

import { passesThrough as firewallPassesThrough } from './Firewall.js';

  /**
   * Check if a point is inside an axis-aligned bounding box (AABB)
   * @param {number} px - Point X coordinate
   * @param {number} py - Point Y coordinate
   * @param {number} boxCenterX - Box center X
   * @param {number} boxCenterY - Box center Y
   * @param {number} boxWidth - Box width
   * @param {number} boxHeight - Box height
   * @returns {boolean}
   */
  function pointInAABB(px, py, boxCenterX, boxCenterY, boxWidth, boxHeight) {
    const halfWidth = boxWidth / 2;
    const halfHeight = boxHeight / 2;

    return px >= boxCenterX - halfWidth &&
           px <= boxCenterX + halfWidth &&
           py >= boxCenterY - halfHeight &&
           py <= boxCenterY + halfHeight;
  }

  /**
   * Check if a point collides with any solid collision box in world objects
   * @param {number} x - Point X coordinate in world space
   * @param {number} y - Point Y coordinate in world space
   * @param {Map|Array|Object} worldObjects - World objects (Map, Array, or object with values())
   * @param {Object} options - Options for collision check
   * @param {{ includeDoors?: boolean, includeBreakable?: boolean, shooterId?: string, shooterTeam?: number|null }} [options] - includeDoors default false; includeBreakable default true; shooterId/shooterTeam let one-way firewalls be skipped
   * @returns {boolean} - True if point collides with a solid object
   */
  function pointCollidesWithWorld(x, y, worldObjects, options = {}) {
    const includeDoors = options.includeDoors || false;
    const includeBreakable = options.includeBreakable !== false; // Default true
    const shooterId = options.shooterId;
    const shooterTeam = options.shooterTeam ?? null;

    // Handle different worldObjects formats (Map, Array, or iterable)
    const objects = worldObjects.values ? worldObjects.values() :
                    Array.isArray(worldObjects) ? worldObjects :
                    Object.values(worldObjects);

    for (const obj of objects) {
      if (!obj.collision) continue;
      if (obj.passThroughFor && shooterId !== undefined && firewallPassesThrough(obj, shooterId, shooterTeam)) continue;

      for (const coll of obj.collision) {
        const collType = coll.type || 'wall';

        // Skip doors unless explicitly included
        if (collType === 'door' && !includeDoors) continue;

        // Skip breakable unless explicitly included
        if (collType === 'breakable' && !includeBreakable) continue;

        // Calculate collision box center in world coordinates
        const boxCenterX = obj.x + coll.x;
        const boxCenterY = obj.y + coll.y;

        if (pointInAABB(x, y, boxCenterX, boxCenterY, coll.width, coll.height)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if a point is within game boundaries
   * @param {number} x - Point X coordinate
   * @param {number} y - Point Y coordinate
   * @param {number} worldWidth - World width
   * @param {number} worldHeight - World height
   * @param {number} buffer - Buffer distance from edges (default: 0)
   * @returns {boolean} - True if point is within boundaries
   */
  function pointInBounds(x, y, worldWidth, worldHeight, buffer = 0) {
    return x >= buffer &&
           x <= worldWidth - buffer &&
           y >= buffer &&
           y <= worldHeight - buffer;
  }

  /**
   * Get collision info for a point (which object/collision box it hit)
   * @param {number} x - Point X coordinate in world space
   * @param {number} y - Point Y coordinate in world space
   * @param {Map|Array|Object} worldObjects - World objects
   * @param {Object} options - Same options as pointCollidesWithWorld
   * @returns {Object|null} - Collision info { objectId, collisionLabel, collisionType } or null
   */
  function getCollisionAt(x, y, worldObjects, options = {}) {
    const includeDoors = options.includeDoors || false;
    const includeBreakable = options.includeBreakable !== false;

    const objects = worldObjects.values ? worldObjects.values() :
                    Array.isArray(worldObjects) ? worldObjects :
                    Object.values(worldObjects);

    for (const obj of objects) {
      if (!obj.collision) continue;

      for (const coll of obj.collision) {
        const collType = coll.type || 'wall';

        if (collType === 'door' && !includeDoors) continue;
        if (collType === 'breakable' && !includeBreakable) continue;

        const boxCenterX = obj.x + coll.x;
        const boxCenterY = obj.y + coll.y;

        if (pointInAABB(x, y, boxCenterX, boxCenterY, coll.width, coll.height)) {
          return {
            objectId: obj.id,
            collisionLabel: coll.label,
            collisionType: collType
          };
        }
      }
    }

    return null;
  }

  /**
   * Simple line-segment vs AABB intersection test
   * Useful for raycasting / line-of-sight checks
   * @param {number} x1 - Line start X
   * @param {number} y1 - Line start Y
   * @param {number} x2 - Line end X
   * @param {number} y2 - Line end Y
   * @param {number} boxCenterX - Box center X
   * @param {number} boxCenterY - Box center Y
   * @param {number} boxWidth - Box width
   * @param {number} boxHeight - Box height
   * @returns {boolean} - True if line intersects box
   */
  function lineIntersectsAABB(x1, y1, x2, y2, boxCenterX, boxCenterY, boxWidth, boxHeight) {
    const halfWidth = boxWidth / 2;
    const halfHeight = boxHeight / 2;

    const minX = boxCenterX - halfWidth;
    const maxX = boxCenterX + halfWidth;
    const minY = boxCenterY - halfHeight;
    const maxY = boxCenterY + halfHeight;

    // Check if either endpoint is inside the box
    if (pointInAABB(x1, y1, boxCenterX, boxCenterY, boxWidth, boxHeight) ||
        pointInAABB(x2, y2, boxCenterX, boxCenterY, boxWidth, boxHeight)) {
      return true;
    }

    // Liang-Barsky algorithm for line-box intersection
    const dx = x2 - x1;
    const dy = y2 - y1;

    let tMin = 0;
    let tMax = 1;

    // Check X planes
    if (dx !== 0) {
      const t1 = (minX - x1) / dx;
      const t2 = (maxX - x1) / dx;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    } else if (x1 < minX || x1 > maxX) {
      return false;
    }

    // Check Y planes
    if (dy !== 0) {
      const t1 = (minY - y1) / dy;
      const t2 = (maxY - y1) / dy;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    } else if (y1 < minY || y1 > maxY) {
      return false;
    }

    return tMax >= tMin;
  }

  /**
   * Get the entry point (t value) where a line segment enters an AABB
   * Returns the parametric t value (0-1) where intersection occurs, or null if no intersection
   * t=0 means start of line, t=1 means end of line
   * @returns {number|null} - Parametric t value of entry point, or null
   */
  function getLineAABBEntryT(x1, y1, x2, y2, boxCenterX, boxCenterY, boxWidth, boxHeight) {
    const halfWidth = boxWidth / 2;
    const halfHeight = boxHeight / 2;

    const minX = boxCenterX - halfWidth;
    const maxX = boxCenterX + halfWidth;
    const minY = boxCenterY - halfHeight;
    const maxY = boxCenterY + halfHeight;

    // If start point is inside the box, collision is at t=0
    if (pointInAABB(x1, y1, boxCenterX, boxCenterY, boxWidth, boxHeight)) {
      return 0;
    }

    const dx = x2 - x1;
    const dy = y2 - y1;

    let tMin = 0;
    let tMax = 1;

    // Check X planes
    if (dx !== 0) {
      const t1 = (minX - x1) / dx;
      const t2 = (maxX - x1) / dx;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    } else if (x1 < minX || x1 > maxX) {
      return null;
    }

    // Check Y planes
    if (dy !== 0) {
      const t1 = (minY - y1) / dy;
      const t2 = (maxY - y1) / dy;
      tMin = Math.max(tMin, Math.min(t1, t2));
      tMax = Math.min(tMax, Math.max(t1, t2));
    } else if (y1 < minY || y1 > maxY) {
      return null;
    }

    // If valid intersection exists, return entry point
    if (tMax >= tMin && tMin <= 1 && tMax >= 0) {
      return Math.max(0, tMin);
    }

    return null;
  }

  /**
   * Find the first collision point along a ray through world objects
   * Used for precomputing projectile termination points
   * @param {number} x1 - Ray start X
   * @param {number} y1 - Ray start Y
   * @param {number} x2 - Ray end X (max distance)
   * @param {number} y2 - Ray end Y (max distance)
   * @param {Map|Array|Object} worldObjects - World objects
   * @param {Object} options - Same options as pointCollidesWithWorld
   * @returns {Object|null} - { x, y, t } of first collision, or null if no collision
   */
  function getFirstCollisionPoint(x1, y1, x2, y2, worldObjects, options = {}) {
    const includeDoors = options.includeDoors || false;
    const includeBreakable = options.includeBreakable !== false;
    // One-way objects (firewalls): a shot from the owner/team passes through. Mirrors the server's rule.
    const shooterId = options.shooterId;
    const shooterTeam = options.shooterTeam ?? null;

    const objects = worldObjects.values ? worldObjects.values() :
                    Array.isArray(worldObjects) ? worldObjects :
                    Object.values(worldObjects);

    let closestT = Infinity;
    let closestPoint = null;

    for (const obj of objects) {
      if (!obj.collision) continue;
      if (obj.passThroughFor && shooterId !== undefined && firewallPassesThrough(obj, shooterId, shooterTeam)) continue;

      for (const coll of obj.collision) {
        const collType = coll.type || 'wall';

        if (collType === 'door' && !includeDoors) continue;
        if (collType === 'breakable' && !includeBreakable) continue;

        const boxCenterX = obj.x + coll.x;
        const boxCenterY = obj.y + coll.y;

        const t = getLineAABBEntryT(x1, y1, x2, y2, boxCenterX, boxCenterY, coll.width, coll.height);

        if (t !== null && t < closestT) {
          closestT = t;
          closestPoint = {
            x: x1 + (x2 - x1) * t,
            y: y1 + (y2 - y1) * t,
            t: t
          };
        }
      }
    }

    return closestPoint;
  }

  /**
   * Check if a line segment collides with any world object
   * @param {number} x1 - Line start X
   * @param {number} y1 - Line start Y
   * @param {number} x2 - Line end X
   * @param {number} y2 - Line end Y
   * @param {Map|Array|Object} worldObjects - World objects
   * @param {Object} options - Same options as pointCollidesWithWorld
   * @returns {boolean}
   */
  function lineCollidesWithWorld(x1, y1, x2, y2, worldObjects, options = {}) {
    const includeDoors = options.includeDoors || false;
    const includeBreakable = options.includeBreakable !== false;

    const objects = worldObjects.values ? worldObjects.values() :
                    Array.isArray(worldObjects) ? worldObjects :
                    Object.values(worldObjects);

    for (const obj of objects) {
      if (!obj.collision) continue;

      for (const coll of obj.collision) {
        const collType = coll.type || 'wall';

        if (collType === 'door' && !includeDoors) continue;
        if (collType === 'breakable' && !includeBreakable) continue;

        const boxCenterX = obj.x + coll.x;
        const boxCenterY = obj.y + coll.y;

        if (lineIntersectsAABB(x1, y1, x2, y2, boxCenterX, boxCenterY, coll.width, coll.height)) {
          return true;
        }
      }
    }

    return false;
  }

  // ─────────────────────────────────────────────────────────────
  // EXPORTS
  // ─────────────────────────────────────────────────────────────
  const CollisionUtils = {
    pointInAABB,
    pointCollidesWithWorld,
    pointInBounds,
    getCollisionAt,
    lineIntersectsAABB,
    getLineAABBEntryT,
    lineCollidesWithWorld,
    getFirstCollisionPoint
  };

export { CollisionUtils };
